// src/components/Teams/MilitaryBatchManager.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import config from '../../../config';
import { toast } from '../../Common/Toast';
import MilitarySessionVideoCall from './MilitarySessionVideoCall';
import './MilitaryBatchManager.css';

const MilitaryBatchManager = ({ 
  batchId, 
  token, 
  teamId, 
  teamName,
  user,
  onClose,
  onBatchComplete 
}) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [batchStatus, setBatchStatus] = useState('active');
  const socketRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const mountedRef = useRef(true);
  
  const fetchBatchDetails = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    
    try {
      const response = await axios.get(
        `${config.baseURL}/api/military/batch/${batchId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && mountedRef.current) {
        const mappedCandidates = response.data.candidates.map(c => ({
          ...c,
          is_online: false,
          last_update: new Date().toISOString()
        }));
        setCandidates(mappedCandidates);
      } else if (!response.data.success && !silent) {
        toast.error(response.data.message || 'Failed to load candidates');
      }
    } catch (error) {
      console.error('Error fetching batch details:', error);
      if (!silent && mountedRef.current) {
        toast.error('Failed to load candidates');
      }
    } finally {
      if (mountedRef.current && !silent) {
        setLoading(false);
      }
    }
  }, [batchId, token]);

  const setupSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    
    socketRef.current = io(config.baseURL, {
      auth: { token },
      transports: ["websocket", "polling"]
    });
    
    socketRef.current.on('connect', () => {
      console.log('🪖 Admin connected to batch room:', batchId);
      socketRef.current.emit('join-batch-room', { batchId });
    });
    
    socketRef.current.on('candidate-progress', (data) => {
      if (!mountedRef.current) return;
      setCandidates(prev => prev.map(c => 
        c.program_id === data.program_id 
          ? { ...c, answered_count: data.answered_count, progress: data.progress, last_update: data.timestamp }
          : c
      ));
    });
    
    socketRef.current.on('candidate-removed', (data) => {
      if (!mountedRef.current) return;
      setCandidates(prev => prev.filter(c => c.program_id !== data.program_id));
      toast.info(`${data.candidate_name} removed from session`);
    });
    
    socketRef.current.on('batch-ended', (data) => {
      if (!mountedRef.current) return;
      setBatchStatus('ended');
      toast.info(`Batch session ended - ${data.decision}`);
      onBatchComplete?.(data);
    });
    
    socketRef.current.on('candidate-connected', (data) => {
      if (!mountedRef.current) return;
      setCandidates(prev => prev.map(c => 
        c.program_id === data.program_id ? { ...c, is_online: true } : c
      ));
    });
    
    socketRef.current.on('candidate-disconnected', (data) => {
      if (!mountedRef.current) return;
      setCandidates(prev => prev.map(c => 
        c.program_id === data.program_id ? { ...c, is_online: false } : c
      ));
    });
  }, [batchId, token, onBatchComplete]);
  
  useEffect(() => {
    mountedRef.current = true;
    
    if (batchId) {
      fetchBatchDetails();
      setupSocket();
      
      refreshIntervalRef.current = setInterval(() => {
        fetchBatchDetails(true);
      }, 30000);
    }
    
    return () => {
      mountedRef.current = false;
      if (socketRef.current) {
        socketRef.current.emit('leave-batch-room', { batchId });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [batchId, fetchBatchDetails, setupSocket]);
  
  const handleRemoveCandidate = async (programId, candidateName) => {
    if (!window.confirm(`Remove ${candidateName} from assessment?`)) return;
    
    try {
      await axios.post(
        `${config.baseURL}/api/military/programs/${programId}/remove`,
        { reason: 'Removed by admin' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCandidates(prev => prev.filter(c => c.program_id !== programId));
      toast.success(`${candidateName} removed`);
    } catch (error) {
      console.error('Error removing candidate:', error);
      toast.error('Failed to remove candidate');
    }
  };
  
  const handleEndBatch = async (decision) => {
    const actionText = decision === 'accept_all' ? 'accept' : 
                      decision === 'reject_all' ? 'reject' : 'keep pending';
    
    if (!window.confirm(`End session and ${actionText} all candidates?`)) return;
    
    try {
      await axios.post(
        `${config.baseURL}/api/military/batch/${batchId}/end`,
        { decision },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Batch session ended - ${actionText}`);
      setBatchStatus('ended');
      
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error ending batch:', error);
      toast.error('Failed to end session');
    }
  };
  
  const handleMonitorCandidate = (candidate) => {
    if (!candidate.session_id) {
      toast.error('No active session for this candidate');
      return;
    }
    
    setSelectedCandidate(candidate);
    setActiveSessionId(candidate.session_id);
    setShowVideoCall(true);
  };
  
  const handleCloseVideoCall = () => {
    setShowVideoCall(false);
    setActiveSessionId(null);
    setSelectedCandidate(null);
    fetchBatchDetails(true);
  };
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return '#4caf50';
      case 'pending': return '#ff9800';
      case 'completed': return '#2196f3';
      case 'cancelled': return '#f44336';
      default: return '#9e9e9e';
    }
  };
  
  if (loading) {
    return (
      <div className="batch-manager-overlay">
        <div className="batch-manager-modal">
          <div className="batch-loading">
            <div className="spinner"></div>
            <p>Loading batch session...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (batchStatus === 'ended') {
    return (
      <div className="batch-manager-overlay">
        <div className="batch-manager-modal">
          <div className="batch-completed">
            <div className="completed-icon">✓</div>
            <h2>Batch Session Ended</h2>
            <p>The assessment session has been completed</p>
            <button className="close-btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <div className="batch-manager-overlay">
        <div className="batch-manager-modal">
          <div className="batch-header">
            <div className="batch-title-section">
              <h2>Military Assessment - {teamName}</h2>
              <div className="batch-id">Batch: {batchId?.slice(-8) || batchId}</div>
            </div>
            
            <div className="batch-stats">
              <div className="stat-card">
                <span className="stat-label">Total</span>
                <span className="stat-value">{candidates.length}</span>
              </div>
              <div className="stat-card active">
                <span className="stat-label">Active</span>
                <span className="stat-value">{candidates.filter(c => c.session_status === 'active').length}</span>
              </div>
              <div className="stat-card online">
                <span className="stat-label">Online</span>
                <span className="stat-value">{candidates.filter(c => c.is_online).length}</span>
              </div>
            </div>
            
            <button className="close-modal-btn" onClick={onClose}>×</button>
          </div>
          
          <div className="batch-body">
            {candidates.length === 0 ? (
              <div className="no-candidates">
                <p>No active candidates in this batch</p>
                <button className="close-btn" onClick={() => fetchBatchDetails()}>Retry</button>
              </div>
            ) : (
              <div className="candidates-container">
                {candidates.map(candidate => (
                  <div key={candidate.program_id} className={`candidate-card ${candidate.is_online ? 'online' : ''}`}>
                    <div className="candidate-card-header">
                      <div className="candidate-avatar">
                        {candidate.candidate_name?.charAt(0) || 'C'}
                        {candidate.is_online && <span className="online-indicator"></span>}
                      </div>
                      <div className="candidate-info">
                        <h3>{candidate.candidate_name || 'Unknown Candidate'}</h3>
                        <span className="candidate-email">{candidate.candidate_email}</span>
                      </div>
                      <button className="remove-candidate-icon" onClick={() => handleRemoveCandidate(candidate.program_id, candidate.candidate_name)} title="Remove candidate">✕</button>
                    </div>
                    
                    <div className="candidate-progress-section">
                      <div className="progress-header">
                        <span className="week-badge">Week {candidate.current_week}/6</span>
                        <span className="questions-progress">{candidate.answered_count}/{candidate.total_questions} Questions</span>
                      </div>
                      
                      <div className="progress-bar-container">
                        <div className="progress-bar-fill" style={{ width: `${candidate.progress || 0}%`, background: getStatusColor(candidate.session_status) }} />
                      </div>
                      
                      <div className="progress-footer">
                        <span className={`status-badge ${candidate.session_status}`}>{candidate.session_status}</span>
                        <span className="progress-percentage">{candidate.progress || 0}%</span>
                      </div>
                    </div>
                    
                    <div className="candidate-actions">
                      <button className={`monitor-btn ${!candidate.session_id ? 'disabled' : ''}`} onClick={() => handleMonitorCandidate(candidate)} disabled={!candidate.session_id}>
                        {candidate.is_online ? '👁️ Monitor Live' : '📊 View Progress'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="batch-footer">
            <div className="batch-actions-group">
              <button className="batch-action accept-all" onClick={() => handleEndBatch('accept_all')} disabled={candidates.length === 0}>✅ Accept All</button>
              <button className="batch-action reject-all" onClick={() => handleEndBatch('reject_all')} disabled={candidates.length === 0}>❌ Reject All</button>
              <button className="batch-action keep-pending" onClick={() => handleEndBatch('pending')} disabled={candidates.length === 0}>⏸️ Keep Pending</button>
            </div>
            <button className="cancel-batch-btn" onClick={onClose}>Close Manager</button>
          </div>
        </div>
      </div>
      
      {showVideoCall && activeSessionId && selectedCandidate && (
        <div className="monitor-video-overlay">
          <div className="monitor-video-container">
            <MilitarySessionVideoCall
              sessionId={activeSessionId}
              programId={selectedCandidate.program_id}
              weekNumber={selectedCandidate.current_week}
              userId={user?.id}
              token={token}
              user={user}
              selectedUser={{ 
                first_name: selectedCandidate.candidate_name, 
                user_id: selectedCandidate.candidate_id 
              }}
              isAdmin={true}
              teamName={teamName}
              onSessionComplete={() => { 
                handleCloseVideoCall(); 
                fetchBatchDetails(true); 
              }}
              onClose={handleCloseVideoCall}
              isMinimized={false}
              onToggleMinimize={() => {
                // Optional: implement minimize if needed
                console.log('Minimize clicked');
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default MilitaryBatchManager;





// // src/components/Teams/MilitaryBatchManager.jsx
// import React, { useState, useEffect, useRef } from 'react';
// import axios from 'axios';
// import io from 'socket.io-client';
// import config from '../../config';
// import { toast } from '../Common/Toast';
// import MilitarySessionVideoCall from './MilitarySessionVideoCall';
// import './MilitaryBatchManager.css';

// const MilitaryBatchManager = ({ 
//   batchId, 
//   token, 
//   teamId, 
//   teamName,
//   user,
//   onClose,
//   onBatchComplete,
//   preloadedCandidates = null // New prop for reopening 
// }) => {
//   const [candidates, setCandidates] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [selectedCandidate, setSelectedCandidate] = useState(null);
//   const [showVideoCall, setShowVideoCall] = useState(false);
//   const [activeSessionId, setActiveSessionId] = useState(null);
//   const [batchStatus, setBatchStatus] = useState('active');
//   const socketRef = useRef(null);
//   const refreshIntervalRef = useRef(null);
  
//   useEffect(() => {
//     if (batchId && !preloadedCandidates) {
//       fetchBatchDetails();
//       setupSocket();
      
//       // Refresh batch details every 30 seconds
//       refreshIntervalRef.current = setInterval(() => {
//         fetchBatchDetails(true);
//       }, 30000);
//     } else if (preloadedCandidates) {
//       setupSocket();
//     }
    
//     return () => {
//       if (socketRef.current) {
//         socketRef.current.disconnect();
//       }
//       if (refreshIntervalRef.current) {
//         clearInterval(refreshIntervalRef.current);
//       }
//     };
//   }, [batchId, preloadedCandidates]);
  
//   const setupSocket = () => {
//     socketRef.current = io(config.baseURL, {
//       auth: { token },
//       transports: ["websocket", "polling"]
//     });
    
//     socketRef.current.on('connect', () => {
//       console.log('🪖 Admin connected to batch room:', batchId);
//       socketRef.current.emit('join-batch-room', { batchId });
//     });
    
//     socketRef.current.on('candidate-progress', (data) => {
//       setCandidates(prev => prev.map(c => 
//         c.program_id === data.program_id 
//           ? { 
//               ...c, 
//               answered_count: data.answered_count, 
//               progress: data.progress,
//               last_update: data.timestamp
//             }
//           : c
//       ));
//     });
    
//     socketRef.current.on('candidate-removed', (data) => {
//       setCandidates(prev => prev.filter(c => c.program_id !== data.program_id));
//       toast.info(`${data.candidate_name} removed from session`);
//     });
    
//     socketRef.current.on('batch-ended', (data) => {
//       setBatchStatus('ended');
//       toast.info(`Batch session ended - ${data.decision}`);
//       if (onBatchComplete) {
//         onBatchComplete(data);
//       }
//     });
    
//     socketRef.current.on('candidate-connected', (data) => {
//       setCandidates(prev => prev.map(c => 
//         c.program_id === data.program_id 
//           ? { ...c, is_online: true }
//           : c
//       ));
//     });
    
//     socketRef.current.on('candidate-disconnected', (data) => {
//       setCandidates(prev => prev.map(c => 
//         c.program_id === data.program_id 
//           ? { ...c, is_online: false }
//           : c
//       ));
//     });
//   };
  
//   const fetchBatchDetails = async (silent = false) => {
//     if (!silent) setLoading(true);
    
//     try {
//       const response = await axios.get(
//         `${config.baseURL}/api/military/batch/${batchId}`,
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
      
//       if (response.data.success) {
//         setCandidates(response.data.candidates.map(c => ({
//           ...c,
//           is_online: false,
//           last_update: new Date().toISOString()
//         })));
//       }
//     } catch (error) {
//       console.error('Error fetching batch details:', error);
//       if (!silent) {
//         toast.error('Failed to load candidates');
//       }
//     } finally {
//       if (!silent) setLoading(false);
//     }
//   };
  
//   const handleRemoveCandidate = async (programId, candidateName) => {
//     if (!window.confirm(`Remove ${candidateName} from assessment?`)) return;
    
//     try {
//       await axios.post(
//         `${config.baseURL}/api/military/programs/${programId}/remove`,
//         { reason: 'Removed by admin' },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
      
//       setCandidates(prev => prev.filter(c => c.program_id !== programId));
//       toast.success(`${candidateName} removed`);
//     } catch (error) {
//       console.error('Error removing candidate:', error);
//       toast.error('Failed to remove candidate');
//     }
//   };
  
//   const handleEndBatch = async (decision) => {
//     const actionText = decision === 'accept_all' ? 'accept' : 
//                       decision === 'reject_all' ? 'reject' : 'keep pending';
    
//     if (!window.confirm(`End session and ${actionText} all candidates?`)) return;
    
//     try {
//       await axios.post(
//         `${config.baseURL}/api/military/batch/${batchId}/end`,
//         { decision },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
      
//       toast.success(`Batch session ended - ${actionText}`);
//       setBatchStatus('ended');
      
//       setTimeout(() => {
//         onClose();
//       }, 2000);
//     } catch (error) {
//       console.error('Error ending batch:', error);
//       toast.error('Failed to end session');
//     }
//   };
  
//   const handleMonitorCandidate = (candidate) => {
//     if (!candidate.session_id) {
//       toast.error('No active session for this candidate');
//       return;
//     }
    
//     setSelectedCandidate(candidate);
//     setActiveSessionId(candidate.session_id);
//     setShowVideoCall(true);
//   };
  
//   const handleCloseVideoCall = () => {
//     setShowVideoCall(false);
//     setActiveSessionId(null);
//     setSelectedCandidate(null);
//     // Refresh candidate list after monitoring
//     fetchBatchDetails(true);
//   };
  
//   const getStatusColor = (status) => {
//     switch(status) {
//       case 'active': return '#4caf50';
//       case 'pending': return '#ff9800';
//       case 'completed': return '#2196f3';
//       case 'cancelled': return '#f44336';
//       default: return '#9e9e9e';
//     }
//   };
  
//   if (loading) {
//     return (
//       <div className="batch-manager-overlay">
//         <div className="batch-manager-modal">
//           <div className="batch-loading">
//             <div className="spinner"></div>
//             <p>Loading batch session...</p>
//           </div>
//         </div>
//       </div>
//     );
//   }
  
//   if (batchStatus === 'ended') {
//     return (
//       <div className="batch-manager-overlay">
//         <div className="batch-manager-modal">
//           <div className="batch-completed">
//             <div className="completed-icon">✓</div>
//             <h2>Batch Session Ended</h2>
//             <p>The assessment session has been completed</p>
//             <button className="close-batch-btn" onClick={onClose}>
//               Close
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   }
  
//   return (
//     <>
//       <div className="batch-manager-overlay">
//         <div className="batch-manager-modal">
//           <div className="batch-header">
//             <div className="batch-title-section">
//               <h2>Military Assessment - {teamName}</h2>
//               <div className="batch-id">Batch ID: {batchId}</div>
//             </div>
            
//             <div className="batch-stats">
//               <div className="stat-card">
//                 <span className="stat-label">Total</span>
//                 <span className="stat-value">{candidates.length}</span>
//               </div>
//               <div className="stat-card active">
//                 <span className="stat-label">Active</span>
//                 <span className="stat-value">
//                   {candidates.filter(c => c.session_status === 'active').length}
//                 </span>
//               </div>
//               <div className="stat-card online">
//                 <span className="stat-label">Online</span>
//                 <span className="stat-value">
//                   {candidates.filter(c => c.is_online).length}
//                 </span>
//               </div>
//             </div>
            
//             <button className="close-modal-btn" onClick={onClose}>×</button>
//           </div>
          
//           <div className="batch-body">
//             {candidates.length === 0 ? (
//               <div className="no-candidates">
//                 <p>No active candidates in this batch</p>
//               </div>
//             ) : (
//               <div className="candidates-container">
//                 {candidates.map(candidate => (
//                   <div 
//                     key={candidate.program_id} 
//                     className={`candidate-card ${candidate.is_online ? 'online' : ''}`}
//                   >
//                     <div className="candidate-card-header">
//                       <div className="candidate-avatar">
//                         {candidate.candidate_name?.charAt(0) || 'C'}
//                         {candidate.is_online && <span className="online-indicator"></span>}
//                       </div>
//                       <div className="candidate-info">
//                         <h3>{candidate.candidate_name || 'Unknown Candidate'}</h3>
//                         <span className="candidate-email">{candidate.candidate_email}</span>
//                       </div>
//                       <button 
//                         className="remove-candidate-icon"
//                         onClick={() => handleRemoveCandidate(
//                           candidate.program_id, 
//                           candidate.candidate_name
//                         )}
//                         title="Remove candidate"
//                       >
//                         ✕
//                       </button>
//                     </div>
                    
//                     <div className="candidate-progress-section">
//                       <div className="progress-header">
//                         <span className="week-badge">Week {candidate.current_week}/6</span>
//                         <span className="questions-progress">
//                           {candidate.answered_count}/{candidate.total_questions} Questions
//                         </span>
//                       </div>
                      
//                       <div className="progress-bar-container">
//                         <div 
//                           className="progress-bar-fill"
//                           style={{ 
//                             width: `${candidate.progress}%`,
//                             background: getStatusColor(candidate.session_status)
//                           }}
//                         />
//                       </div>
                      
//                       <div className="progress-footer">
//                         <span className={`status-badge ${candidate.session_status}`}>
//                           {candidate.session_status}
//                         </span>
//                         <span className="progress-percentage">{candidate.progress}%</span>
//                       </div>
//                     </div>
                    
//                     <div className="candidate-actions">
//                       <button 
//                         className={`monitor-btn ${!candidate.session_id ? 'disabled' : ''}`}
//                         onClick={() => handleMonitorCandidate(candidate)}
//                         disabled={!candidate.session_id}
//                       >
//                         {candidate.is_online ? '👁️ Monitor Live' : '📊 View Progress'}
//                       </button>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>
          
//           <div className="batch-footer">
//             <div className="batch-actions-group">
//               <button 
//                 className="batch-action accept-all"
//                 onClick={() => handleEndBatch('accept_all')}
//                 disabled={candidates.length === 0}
//               >
//                 ✅ Accept All & End Session
//               </button>
//               <button 
//                 className="batch-action reject-all"
//                 onClick={() => handleEndBatch('reject_all')}
//                 disabled={candidates.length === 0}
//               >
//                 ❌ Reject All & End
//               </button>
//               <button 
//                 className="batch-action keep-pending"
//                 onClick={() => handleEndBatch('pending')}
//                 disabled={candidates.length === 0}
//               >
//                 ⏸️ Keep Pending
//               </button>
//             </div>
//             <button className="cancel-batch-btn" onClick={onClose}>
//               Close Manager
//             </button>
//           </div>
//         </div>
//       </div>
      
//       {/* Video Call Modal for monitoring individual candidate */}
//       {showVideoCall && activeSessionId && selectedCandidate && (
//         <div className="monitor-video-overlay">
//           <div className="monitor-video-container">
//             <MilitarySessionVideoCall
//               sessionId={activeSessionId}
//               programId={selectedCandidate.program_id}
//               weekNumber={selectedCandidate.current_week}
//               userId={user?.id}
//               token={token}
//               user={user}
//               selectedUser={{
//                 first_name: selectedCandidate.candidate_name,
//                 user_id: selectedCandidate.candidate_id
//               }}
//               isAdmin={true}
//               teamName={teamName}
//               onSessionComplete={() => {
//                 handleCloseVideoCall();
//                 fetchBatchDetails(true);
//               }}
//               onClose={handleCloseVideoCall}
//               isMinimized={false}
//               onToggleMinimize={() => {}}
//             />
//           </div>
//         </div>
//       )}
//     </>
//   );
// };

// export default MilitaryBatchManager;